/**
 * Plot pipeline data model (docs/opencadstudio-parity/02-plot-pdf-ctb.md §3.1).
 * TypeScript translation of the OCS page model (src/io/plot.rs): a PlotPage
 * is one sheet of PDF output — paper geometry in millimetres, plot content
 * in source units (paper-space metres for layout plots, model-space metres
 * for extents/display/window plots) and the scale that maps one onto the
 * other.
 */

/** DPI target for rasterized content (OCS quality ladder). */
export type PlotQuality = 'low' | 'medium' | 'high';

/** Plot option switches (OCS PlotOptions). */
export interface PlotOptions {
    /** Use entity/layer lineweights; false = uniform hairline. */
    objectLineweights: boolean;
    /** Multiply resolved lineweights by the plot scale factor. */
    scaleLineweights: boolean;
    /** Honor entity transparency (0 otherwise). */
    plotTransparency: boolean;
    /** Overprint strokes with a multiply blend mode (OCS merge_lines). */
    mergeLines: boolean;
    /** Stamp file name + date at the bottom edge of the sheet. */
    plotStamp: boolean;
    quality: PlotQuality;
}

export const DEFAULT_PLOT_OPTIONS: PlotOptions = {
    objectLineweights: true,
    scaleLineweights: false,
    plotTransparency: true,
    mergeLines: false,
    plotStamp: false,
    quality: 'medium',
};

/** What region of the document the page frames (OCS PlotContent kinds). */
export type PlotAreaKind = 'layout' | 'extents' | 'window' | 'display';

/** Model-space window (source units) for plotType 'window'. */
export interface PlotWindow {
    x1: number;
    y1: number;
    x2: number;
    y2: number;
}

/**
 * Named page setup persisted per layout (OCS PlotSettings subset).
 * Paper geometry is stored in millimetres; the sheet measures
 * paperWidth × paperHeight after the rotation is applied.
 */
export interface PageSetup {
    name: string;
    /** Display name of the paper format ('ISO A4 (297 x 210 mm)', …). */
    paperName: string;
    /** Final sheet width in mm (rotation already folded in). */
    paperWidth: number;
    /** Final sheet height in mm. */
    paperHeight: number;
    units: 'mm';
    /** Content rotation on the sheet: 0/90/180/270 degrees. */
    rotation: 0 | 90 | 180 | 270;
    /** Fit the plot area to the sheet (overrides scaleNum/scaleDen). */
    fitToPaper: boolean;
    /** Standard/custom scale num:den (paper units per model units). */
    scaleNum: number;
    scaleDen: number;
    /** Interpretation of drawing units for the scale (app default metres). */
    drawingUnits: 'mm' | 'cm' | 'm' | 'in';
    /** Plot offset in mm from the lower-left sheet corner. */
    offsetX: number;
    offsetY: number;
    /** Center the plot on the sheet (offsets ignored). */
    centerPlot: boolean;
    /** CTB table name; '' = object colors. */
    plotStyleName: string;
    plotType: PlotAreaKind;
    plotWindow?: PlotWindow;
    options: PlotOptions;
}

/** One clipped stroke segment of the plot content (model/paper space). */
export interface PlotWire {
    kind: 'wire';
    /** Polyline vertices in source units (metres). */
    points: [number, number][];
    /** Closed flag — wires render open polylines; fills carry rings. */
    /** True-color hex ('#rrggbb'); CTB overrides at render time. */
    color: string;
    /** Physical lineweight in mm BEFORE CTB resolution (0 = hairline). */
    lineweight: number;
    /** Linetype dash pattern in source units, null = continuous. */
    dash: [number, number] | null;
    /** 0-1 opacity (transparency option off → forced 1). */
    opacity: number;
    /** Draw order within the content (DRAWORDER). */
    sequence: number;
    /** Clip polygon in source units; null = page window only. */
    clip: [number, number][] | null;
    /** ACI used for CTB lookups (nearest match of color). */
    aci: number;
}

/** Solid fill / wipeout plate drawn before wires. */
export interface PlotFill {
    kind: 'fill';
    /** Outer ring + optional holes, in source units. */
    ring: [number, number][];
    holes: [number, number][][];
    color: string;
    /** 'wipeout' plates paint in the paper background color. */
    role: 'fill' | 'wipeout';
    opacity: number;
    sequence: number;
    clip: [number, number][] | null;
    aci: number;
}

/** Hatch pattern strokes (pre-tessellated segments from the entity bag). */
export interface PlotHatch {
    kind: 'hatch';
    ring: [number, number][];
    holes: [number, number][][];
    /** Pattern segments (each a 2-point stroke) in source units. */
    segments: [number, number][][];
    /** Solid fill when the hatch carries no pattern (FILLMODE parity). */
    solid: boolean;
    color: string;
    opacity: number;
    sequence: number;
    clip: [number, number][] | null;
    aci: number;
}

/** A text run placed at its baseline anchor in source units. */
export interface PlotText {
    kind: 'text';
    x: number;
    y: number;
    /** Degrees counter-clockwise. */
    rotation: number;
    /** Cap height in source units. */
    height: number;
    content: string;
    color: string;
    opacity: number;
    sequence: number;
    clip: [number, number][] | null;
    aci: number;
}

export type PlotItem = PlotWire | PlotFill | PlotHatch | PlotText;

/** Plot content of one page, all items in source units. */
export interface PlotContent {
    items: PlotItem[];
    /** Source-space bounds of the content (metres). */
    bounds: { minX: number; minY: number; maxX: number; maxY: number } | null;
}

/** One output sheet: content + paper mapping (OCS PlotPage). */
export interface PlotPage {
    /** Sheet title used for the stamp + progress messages. */
    title: string;
    content: PlotContent;
    paperWidth: number;
    paperHeight: number;
    rotation: 0 | 90 | 180 | 270;
    /** Millimetres per source unit (1000 for a 1:1 layout plot). */
    scale: number;
    /** Source-space point mapped onto the plot origin (offset point). */
    originX: number;
    originY: number;
    offsetX: number;
    offsetY: number;
    options: PlotOptions;
    plotStyleName: string;
    /** Sheet label for the stamp line. */
    stampLabel?: string;
}

/** CTB/STB style entry resolved per ACI (OCS PlotStyleEntry subset). */
export interface PlotStyleEntry {
    name: string;
    localizedName?: string;
    description?: string;
    /** 0-100 ink screening; 100 = full color. */
    screening: number;
    /** Lineweight in mm; null = use object lineweight. */
    lineweight: number | null;
    /** Packed RGB color; null = use object color. */
    color: number | null;
    /** index 250-255 gray level used when colorPolicy = grayscale. */
    colorPolicy: number;
    physicalPenNumber: number;
    virtualPenNumber: number;
    linepatternSize: number;
    linetype: number;
    adaptiveLinetype: boolean;
    fillStyle: number;
    endStyle: number;
    joinStyle: number;
}

/** Parsed plot style table (CTB) with tolerant-load diagnostics. */
export interface PlotStyleTable {
    name: string;
    /** true for STB tables (named styles — plot resolution rejected). */
    isStb: boolean;
    scaleFactor: number;
    applyFactor: boolean;
    /** 256 ACI entries, index 0 = BYBLOCK passthrough. */
    aciEntries: (PlotStyleEntry | null)[];
    /** Custom lineweight table in mm (index → mm). */
    lineweights: number[];
    loadWarnings: string[];
}
